import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Archive, Bell, ChevronDown, ChevronRight, ChevronsUpDown, Copy, Home, Keyboard, LogOut, Map, MessageSquare, Plus, Search, Settings, ShieldCheck, Star, StickyNote, Users } from "lucide-react";
import { Avatar, IconButton, Menu } from "@/ui";
import { useEngineQuery, useEngineMutation } from "@/engine/hooks";
import { useSession } from "@/app/session";
import { useCommandBar, useLayoutStore } from "@/app/navigation";
import { CreditChip, networkNavItems, useNetworkBadges } from "@/features/network";
import { buildGroupTree, flattenTree } from "@/features/groups/groupTree";
import { useIsAdmin } from "@/features/admin/useIsAdmin";
import s from "./layout.module.css";

const COLLAPSED_KEY = "hub_nav_groups_collapsed";
function readCollapsed(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

export const CONTACT_DRAG_TYPE = "application/x-hub-contact-ids";

function Item({ to, icon, label, badge, rail, end, onDropIds, depth, toggle }: { to: string; icon: React.ReactNode; label: string; badge?: number; rail?: boolean; end?: boolean; onDropIds?: (ids: string[]) => void; dot?: boolean; depth?: number; toggle?: { open: boolean; onToggle: () => void } }) {
  const [over, setOver] = useState(false);
  return (
    <NavLink
      to={to}
      end={end}
      className={s.navItem}
      title={label}
      style={depth ? { paddingLeft: 10 + depth * 14 } : undefined}
      data-depth={depth || undefined}
      data-drop={over ? "true" : undefined}
      onDragOver={onDropIds ? (e) => { if (e.dataTransfer.types.includes(CONTACT_DRAG_TYPE)) { e.preventDefault(); setOver(true); } } : undefined}
      onDragLeave={onDropIds ? () => setOver(false) : undefined}
      onDrop={onDropIds ? (e) => { e.preventDefault(); setOver(false); const raw = e.dataTransfer.getData(CONTACT_DRAG_TYPE); if (raw) onDropIds(raw.split(",").filter(Boolean)); } : undefined}
    >
      {toggle && !rail && (
        <button type="button" aria-label={toggle.open ? `Collapse ${label}` : `Expand ${label}`} aria-expanded={toggle.open} onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle.onToggle(); }} style={{ border: "none", background: "transparent", padding: 0, margin: "0 -4px 0 -6px", cursor: "pointer", color: "var(--text-tertiary)", display: "inline-flex", width: 14, justifyContent: "center" }}>
          {toggle.open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
      )}
      <span style={{ display: "inline-flex", flex: "none", width: 18, justifyContent: "center" }}>{icon}</span>
      {!rail && <span className={s.navItemLabel}>{label}</span>}
      {!rail && badge ? <span className={s.navBadge}>{badge > 99 ? "99+" : badge}</span> : null}
      {rail && badge ? <span className={s.navDot} /> : null}
    </NavLink>
  );
}

export function NavSidebar({ rail }: { rail: boolean }) {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const session = useSession();
  const commandBar = useCommandBar();
  const setShortcutsOpen = useLayoutStore((st) => st.setShortcutsOpen);
  const setFeedbackOpen = useLayoutStore((st) => st.setFeedbackOpen);
  const groups = useEngineQuery("listGroups", undefined);
  const unread = useEngineQuery("getTimelineUnreadCount", undefined, { refetchInterval: 60_000 });
  const sync = useEngineQuery("getSyncStatus", undefined, { refetchInterval: 30_000 });
  const badges = useNetworkBadges();
  const addToGroup = useEngineMutation("addContactsToGroup", { invalidate: ["listGroups", "getContactsInGroup", "queryContacts", "getContact"] });
  const star = useEngineMutation("setContactStarred", { invalidate: ["queryContacts", "getContact", "listGroups"] });

  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsed);
  const toggleCollapsed = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(Array.from(next)));
      } catch {
        // ignore
      }
      return next;
    });
  const visibleGroups = (groups.data ?? []).filter((g) => !g.isInternal);
  const groupNodes = flattenTree(buildGroupTree(visibleGroups), collapsed);
  const favorites = (groups.data ?? []).find((g) => g.isInternal);
  const workspace = session.workspaces.find((w) => w.id === session.workspaceId);
  const userName = session.user ? [session.user.firstName, session.user.lastName].filter(Boolean).join(" ") || session.user.email : "Not signed in";
  const phase = session.offline ? "signed_out" : sync.data?.phase ?? "idle";
  // A refusal from the server (a suspended account, for one) says why; the local data stays usable either way.
  const syncLabel = session.offline ? "Local only" : sync.data?.errorCode === "account_suspended" ? "Account suspended" : `Sync: ${phase}`;
  const syncTitle = session.offline ? "Local only, not syncing" : phase === "error" && sync.data?.error ? sync.data.error : `Sync: ${phase}`;

  return (
    <>
      <div className={s.navTop}>
        {rail ? (
          <span className={s.workspaceMark}>{(workspace?.name ?? "H").slice(0, 1).toUpperCase()}</span>
        ) : (
          <Menu
            trigger={
              <button className={s.workspaceButton} title="Switch workspace">
                <span className={s.workspaceMark}>{(workspace?.name ?? "H").slice(0, 1).toUpperCase()}</span>
                <span className="u-truncate u-grow">{workspace?.name ?? "Hub"}</span>
                <ChevronsUpDown size={14} className="u-muted" />
              </button>
            }
            items={[
              ...session.workspaces.map((w) => ({ id: w.id, label: w.name + (w.id === session.workspaceId ? "  (current)" : ""), onSelect: () => { session.setWorkspace(w.id); window.location.reload(); } })),
              { id: "div", label: "", divider: true },
              { id: "new", label: "New workspace", icon: <Plus size={14} />, onSelect: () => navigate("/settings/workspace?new=1") },
            ]}
          />
        )}
        {!rail && (
          <IconButton label="Search (/)" onClick={() => navigate("/search")}>
            <Search size={16} />
          </IconButton>
        )}
      </div>
      <div className={s.navScroll}>
        <Item to="/" end icon={<Home size={16} />} label="Home" badge={unread.data || undefined} rail={rail} />
        <Item to="/people" icon={<Users size={16} />} label="People" rail={rail} />
        <Item to="/notes" icon={<StickyNote size={16} />} label="Notes" rail={rail} />
        <Item to="/reminders" icon={<Bell size={16} />} label="Reminders" rail={rail} />
        {networkNavItems.map((n) => {
          const Icon = n.icon;
          const item = <Item key={n.to} to={n.to} icon={<Icon size={16} />} label={n.label} badge={n.badgeKey ? badges[n.badgeKey] || undefined : undefined} rail={rail} />;
          if (n.to !== "/matches" || rail) return item;
          // The credit balance sits beside Matches as its own link (not nested in the NavLink).
          return (
            <div key={n.to} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ flex: 1, minWidth: 0 }}>{item}</div>
              <CreditChip compact={false} />
            </div>
          );
        })}
        {!rail && (
          <>
            <div className={s.navSection}>
              <span>Groups</span>
              <IconButton label="New group" onClick={() => navigate("/group/new")} style={{ width: 20, height: 20 }}>
                <Plus size={14} />
              </IconButton>
            </div>
            {favorites && (
              <Item to={`/group/${favorites.id}`} icon={<Star size={16} />} label={favorites.name} rail={rail} badge={favorites.memberCount || undefined} onDropIds={(ids) => ids.forEach((id) => star.mutate({ contactId: id, starred: true }))} />
            )}
            {groupNodes.map(({ group: g, depth, children }) => (
              <Item key={g.id} to={`/group/${g.id}`} icon={<span className={s.groupSwatch} style={{ background: g.color ?? undefined }} />} label={g.name} rail={rail} depth={depth} toggle={children.length > 0 ? { open: !collapsed.has(g.id), onToggle: () => toggleCollapsed(g.id) } : undefined} badge={g.memberCount || undefined} onDropIds={g.kind === "static" ? (ids) => addToGroup.mutate({ groupId: g.id, contactIds: ids }) : undefined} />
            ))}
            {groups.isSuccess && visibleGroups.length === 0 && <div className="u-small u-muted" style={{ padding: "4px 10px" }}>Drag people here to make a group.</div>}
            <div className={s.navSection}>
              <span>More</span>
            </div>
            <Item to="/people/map" icon={<Map size={16} />} label="Map" rail={rail} />
            <Item to="/people/duplicates" icon={<Copy size={16} />} label="Duplicates" rail={rail} />
            <Item to="/archive" icon={<Archive size={16} />} label="Archive" rail={rail} />
          </>
        )}
        <Item to="/settings" icon={<Settings size={16} />} label="Settings" rail={rail} />
      </div>
      <div className={s.navBottom}>
        <Menu
          align="left"
          trigger={
            <button className={s.userButton} title={userName}>
              <Avatar name={userName} size={24} />
              {!rail && <span className="u-truncate u-grow u-small">{userName}</span>}
              {!rail && <span className={s.syncDot} data-phase={phase} title={syncTitle} />}
            </button>
          }
          items={[
            { id: "settings", label: "Settings", icon: <Settings size={14} />, onSelect: () => navigate("/settings") },
            ...(isAdmin ? [{ id: "admin", label: "Admin", icon: <ShieldCheck size={14} />, onSelect: () => navigate("/admin") }] : []),
            { id: "cmd", label: "Command bar", shortcut: "Mod K", onSelect: () => commandBar.open() },
            { id: "feedback", label: "Send feedback", icon: <MessageSquare size={14} />, onSelect: () => setFeedbackOpen(true) },
            { id: "sync", label: syncLabel, disabled: true },
            { id: "div", label: "", divider: true },
            { id: "signout", label: "Sign out", icon: <LogOut size={14} />, danger: true, onSelect: () => { session.signOut(); navigate("/onboarding"); } },
          ]}
        />
        {!rail && (
          <IconButton label="Keyboard shortcuts (?)" onClick={() => setShortcutsOpen(true)} data-testid="nav-shortcuts">
            <Keyboard size={16} />
          </IconButton>
        )}
      </div>
    </>
  );
}
